import React, {useState, useEffect, useRef} from 'react'
import {Box, Grid, VStack, Link, Text, useBreakpointValue, Collapse} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {renderLayoutsSwitch} from '../../utils/contentRenderer'
import {ChevronDownCheckout, ChevronUpCheckout} from '../../../icons'

const LandingPage = (content) => {
    const mainRegionsContent = content?.MainContentArea
    const LandingPageMarginPerDevice = content?.LandingPageMarginPerDevice
    const [sidebarLinks, setSidebarLinks] = useState([])
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [activeSection, setActiveSection] = useState('')
    const [selectedMobileLabel, setSelectedMobileLabel] = useState('')
    const [isScrolling, setIsScrolling] = useState(false)
    const [isSticky, setIsSticky] = useState(false)
    const isMobile = useBreakpointValue({base: true, lg: false})
    const isFaqPage = content?.Url?.toLowerCase()?.includes('faq')
    const dropdownRef = useRef(null)

    // Generate sidebar links from page content when IsLeftMenuAutomaticallyGenerated is true
    useEffect(() => {
        if (content?.IsLeftMenuAutomaticallyGenerated && mainRegionsContent) {
            const links = []

            const extractLinks = (contentItem, level = 0) => {
                const expandedContent = contentItem?.ContentLink?.Expanded

                // Only extract links for TitleBlock content
                if (
                    expandedContent?.__typename === 'TitleBlock' &&
                    expandedContent?.Title &&
                    expandedContent?.TitleAnchorId
                ) {
                    links.push({
                        id: expandedContent.TitleAnchorId,
                        title: expandedContent.Title,
                        level: level
                    })
                }

                // Check for nested content in various possible structures
                if (expandedContent?.CtaItems) {
                    expandedContent.CtaItems.forEach((item) => {
                        const itemData = item?.Expanded || item
                        if (
                            itemData?.__typename === 'TitleBlock' &&
                            itemData?.Title &&
                            itemData?.TitleAnchorId
                        ) {
                            links.push({
                                id: itemData.TitleAnchorId,
                                title: itemData.Title,
                                level: level + 1
                            })
                        }
                    })
                }
            }

            mainRegionsContent.forEach((item) => extractLinks(item))
            setSidebarLinks(links)
        }
    }, [content, mainRegionsContent])

    // Set first link as active on initial load
    useEffect(() => {
        if (sidebarLinks.length > 0 && !activeSection) {
            setActiveSection(sidebarLinks[0].id)
        }
    }, [sidebarLinks, activeSection])

    // Track sticky state for mobile sidebar
    useEffect(() => {
        if (!isMobile) return

        const handleScroll = () => {
            const sidebarElement = document.getElementById('mobile-sticky-sidebar')
            if (sidebarElement) {
                const rect = sidebarElement.getBoundingClientRect()
                setIsSticky(rect.top <= 46)
            }
        }

        window.addEventListener('scroll', handleScroll)
        handleScroll() // Check initial state

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [isMobile])

    // Handle click outside dropdown to close it on mobile
    useEffect(() => {
        if (!isMobile || !isDropdownOpen) return

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [isMobile, isDropdownOpen])

    // Track active section based on scroll position
    useEffect(() => {
        if (!sidebarLinks.length) return

        const observerOptions = {
            rootMargin: isMobile ? '-170px 0px -66% 0px' : '-100px 0px -66% 0px',
            threshold: 0
        }

        const observerCallback = (entries) => {
            if (isScrolling) return
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id)
                }
            })
        }

        const observer = new IntersectionObserver(observerCallback, observerOptions)

        sidebarLinks.forEach((link) => {
            const element = document.getElementById(link.id)
            if (element) {
                observer.observe(element)
            }
        })

        return () => {
            observer.disconnect()
        }
    }, [sidebarLinks, isMobile, isScrolling])

    const renderSidebar = () => {
        if (isMobile) {
            return (
                <Box
                    id="mobile-sticky-sidebar"
                    width={isSticky ? '100vw' : '100%'}
                    position="sticky"
                    top="46px"
                    left={isSticky ? '0' : 'auto'}
                    right={isSticky ? '0' : 'auto'}
                    marginLeft={isSticky ? '-16px' : '0'}
                    marginRight={isSticky ? '-16px' : '0'}
                    paddingLeft={isSticky ? '16px' : '0'}
                    paddingRight={isSticky ? '16px' : '0'}
                    zIndex="sticky"
                    bg="white"
                    py={4}
                    mb={6}
                    borderBottom="1px solid"
                    borderColor="gray.500"
                >
                    <Box position="relative" ref={dropdownRef}>
                        <Box
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && setIsDropdownOpen(!isDropdownOpen)?.(e)
                            }
                            role="button"
                            tabIndex={0}
                            aria-label={
                                isFaqPage ? 'All Faqs' : 'Terms and condition of use and sale'
                            }
                            cursor="pointer"
                            bg="white"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Text
                                textTransform={isFaqPage ? 'uppercase' : 'none'}
                                fontSize="md"
                                fontWeight="normal"
                            >
                                {selectedMobileLabel ? (
                                    selectedMobileLabel
                                ) : isFaqPage ? (
                                    <FormattedMessage
                                        defaultMessage="All Faqs"
                                        id="landing_page.all_faqs"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Terms and condition of use and sale"
                                        id="landing_page.terms_and_conditions"
                                    />
                                )}
                            </Text>
                            {isDropdownOpen ? <ChevronUpCheckout /> : <ChevronDownCheckout />}
                        </Box>
                        <Collapse in={isDropdownOpen} animateOpacity>
                            <Box
                                position="absolute"
                                top="32px"
                                left={0}
                                right={0}
                                mt={2}
                                bg="white"
                                border="1px solid"
                                borderColor="black"
                                maxHeight="309px"
                                overflowY="auto"
                                py="8px"
                                px="10px"
                                zIndex="dropdown"
                                sx={{
                                    '&::-webkit-scrollbar': {
                                        width: '2px'
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bg: 'transparent'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bg: 'gray.400'
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        bg: 'gray.500'
                                    }
                                }}
                            >
                                {sidebarLinks.map((link, index) => (
                                    <Link
                                        key={link.id}
                                        href={`#${link.id}`}
                                        display="block"
                                        p={2}
                                        mb={2}
                                        fontSize="sm"
                                        color="gray.700"
                                        borderBottom={
                                            index < sidebarLinks.length - 1 ? '1px solid' : 'none'
                                        }
                                        borderBottomColor="black"
                                        pl={3 + link.level * 4}
                                        _hover={{bg: 'gray.50'}}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setActiveSection(link.id)
                                            setSelectedMobileLabel(
                                                link.title
                                                    ?.toLowerCase()
                                                    .replace(/\b\w/g, (l) => l.toUpperCase())
                                            )
                                            setIsDropdownOpen(false)
                                            setIsScrolling(true)
                                            setTimeout(() => {
                                                const element = document.getElementById(link.id)
                                                if (element) {
                                                    const yOffset = -170
                                                    const y =
                                                        element.getBoundingClientRect().top +
                                                        window.pageYOffset +
                                                        yOffset
                                                    window.scrollTo({top: y, behavior: 'smooth'})
                                                    setTimeout(() => {
                                                        setIsScrolling(false)
                                                    }, 1000)
                                                }
                                            }, 100)
                                        }}
                                    >
                                        {link.title
                                            ?.toLowerCase()
                                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                                    </Link>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                </Box>
            )
        }

        return (
            <Box position="sticky" top="48px" height="fit-content" py={4}>
                <VStack align="stretch" spacing={2}>
                    {sidebarLinks.map((link) => {
                        const isActive = activeSection === link.id
                        return (
                            <Link
                                key={link.id}
                                href={`#${link.id}`}
                                fontSize="md"
                                pl={link.level * 4}
                                py={1}
                                color={isActive ? 'black' : 'gray.700'}
                                fontWeight={isActive ? 'medium' : 'normal'}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setActiveSection(link.id)
                                    setIsScrolling(true)
                                    const element = document.getElementById(link.id)
                                    if (element) {
                                        const yOffset = -48
                                        const y =
                                            element.getBoundingClientRect().top +
                                            window.pageYOffset +
                                            yOffset
                                        window.scrollTo({top: y, behavior: 'smooth'})
                                        setTimeout(() => {
                                            setIsScrolling(false)
                                        }, 1000)
                                    }
                                }}
                            >
                                {link.title?.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                            </Link>
                        )
                    })}
                </VStack>
            </Box>
        )
    }

    const renderMainContent = (insertSidebar = false) => {
        const contentElements = []
        let sidebarInserted = false

        // Check if there's a CtaItemListBlock in the content
        const hasCtaItemListBlock = mainRegionsContent?.some(
            (item) => item?.ContentLink?.Expanded?.__typename === 'CtaItemListBlock'
        )

        // If no CtaItemListBlock exists, insert sidebar at the beginning
        if (insertSidebar && !hasCtaItemListBlock) {
            contentElements.push(
                <Box key="mobile-sidebar" position="sticky" top="46px" zIndex="sticky" bg="white">
                    {renderSidebar()}
                </Box>
            )
            sidebarInserted = true
        }

        mainRegionsContent?.forEach((mainRegionContent, contentIndex) => {
            const expandedContent = mainRegionContent?.ContentLink?.Expanded

            // Add the content block
            contentElements.push(
                <Box
                    key={contentIndex}
                    id={
                        expandedContent?.__typename === 'TitleBlock' &&
                        expandedContent?.TitleAnchorId
                            ? expandedContent.TitleAnchorId
                            : undefined
                    }
                >
                    {renderLayoutsSwitch(expandedContent, {isLandingPage: true})}
                </Box>
            )

            // Insert sidebar after first CtaItemListBlock on mobile
            if (
                insertSidebar &&
                !sidebarInserted &&
                expandedContent?.__typename === 'CtaItemListBlock'
            ) {
                contentElements.push(
                    <Box
                        key="mobile-sidebar"
                        position="sticky"
                        top="46px"
                        zIndex="sticky"
                        bg="white"
                    >
                        {renderSidebar()}
                    </Box>
                )
                sidebarInserted = true
            }
        })

        return <Box>{contentElements}</Box>
    }

    const marginStyles = {
        base: LandingPageMarginPerDevice?.MarginMobile || '32px 16px 0 16px',
        md: LandingPageMarginPerDevice?.MarginTablet || '32px 16px 0 16px',
        lg: LandingPageMarginPerDevice?.MarginDesktop || '48px 0 0 0'
    }

    if (content?.IsLeftMenuAutomaticallyGenerated && sidebarLinks.length > 0) {
        return (
            <Box data-block-type="LandingPage" pb="267px" margin={marginStyles}>
                {isMobile ? (
                    <VStack spacing={6} align="stretch">
                        {renderMainContent(true)}
                    </VStack>
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns="250px 124px 1fr"
                        gap={0}
                        minHeight="100vh"
                        maxWidth="1300px"
                        mx="auto"
                    >
                        <Box>{renderSidebar()}</Box>
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="stretch"
                            position="relative"
                        >
                            <Box
                                width="1px"
                                height="100%"
                                backgroundColor="grey.400"
                                alignSelf="stretch"
                            />
                        </Box>
                        <Box maxWidth="926px" mt={4}>
                            {renderMainContent()}
                        </Box>
                    </Box>
                )}
            </Box>
        )
    }

    return (
        <Box
            data-block-type="LandingPage"
            display="flex"
            justifyContent="center"
            pb="267px"
            margin={marginStyles}
        >
            <Box maxWidth="926px" width="100%">
                {mainRegionsContent?.map((mainRegionContent, contentIndex) => {
                    return (
                        <React.Fragment key={contentIndex}>
                            {renderLayoutsSwitch(mainRegionContent?.ContentLink?.Expanded, {
                                isLandingPage: true
                            })}
                        </React.Fragment>
                    )
                })}
            </Box>
        </Box>
    )
}

LandingPage.displayName = 'LandingPage'

LandingPage.propTypes = {
    content: PropTypes.object
}

export default LandingPage
